import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Container, SectionHeader, TodaysAppointments } from './UpcomingAppointments.styled';
import { Heading } from 'components/v2/Typography/Heading';
import { Appointment } from 'kb-shared';
import { CycleVisitDaysResponse } from 'screens/Dashboard/Dashboard.types';
import { Badge } from 'screens/Messages/components/Badge/Badge';
import { ModalCancelReschedule } from 'screens/ModalCancelResched/ModalCancelResched';

import { getTodaysAndFutureAppointments } from './UpcomingAppointments.utils';
import { CycleVisits } from '../CycleVisits/CycleVisits';
import { List } from '../List/List';

export const UpcomingAppointments = ({
  appointments,
  nextCycleVisit,
  cycleVisitData
}: {
  appointments?: Appointment[];
  nextCycleVisit?: string;
  cycleVisitData?: CycleVisitDaysResponse;
}) => {
  const [searchParams] = useSearchParams();
  const hasProcessedUrlParam = useRef(false);

  const { todaysAppointments, futureAppointments } = getTodaysAndFutureAppointments(appointments);
  const allUpcomingAppointments = [...todaysAppointments, ...futureAppointments];
  const apptId = searchParams.get('apptId');

  const [selectedAppointmentToDelete, setSelectedAppointmentToDelete] = useState<
    Appointment | undefined
  >();

  useEffect(() => {
    if (!apptId || allUpcomingAppointments.length <= 0 || hasProcessedUrlParam.current) return;

    const matchingAppointment = allUpcomingAppointments.find(appt => appt.id == apptId);

    if (!matchingAppointment) return;

    setSelectedAppointmentToDelete(matchingAppointment);
    hasProcessedUrlParam.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allUpcomingAppointments]);

  if (todaysAppointments.length === 0 && futureAppointments.length === 0 && !nextCycleVisit)
    return <></>;

  return (
    <Container>
      {todaysAppointments.length > 0 && (
        <TodaysAppointments>
          <List
            appointments={todaysAppointments}
            onRescheduledClick={appointment => setSelectedAppointmentToDelete(appointment)}
            onCancelClick={appointment => setSelectedAppointmentToDelete(appointment)}
            highlightTodaysListItems={true}
          />
        </TodaysAppointments>
      )}

      {futureAppointments.length > 0 && (
        <>
          <SectionHeader>
            <Heading tag="div" styledAs="h3" noMargin>
              Upcoming Appointments
            </Heading>
            <Badge label={String(futureAppointments.length)} type="default" size="md" />
          </SectionHeader>
          <List
            appointments={futureAppointments}
            onRescheduledClick={appointment => setSelectedAppointmentToDelete(appointment)}
            onCancelClick={appointment => setSelectedAppointmentToDelete(appointment)}
          />
        </>
      )}

      <CycleVisits nextCycleVisit={nextCycleVisit} cycleVisitData={cycleVisitData} />

      {selectedAppointmentToDelete && (
        <ModalCancelReschedule
          visible={!!selectedAppointmentToDelete}
          onClose={() => setSelectedAppointmentToDelete(undefined)}
          appointment={selectedAppointmentToDelete}
          onAppointmentCancelled={() => window.location.reload()}
        />
      )}
    </Container>
  );
};
